import _isEqual from 'lodash/isEqual'
import { useNextSanityImage } from 'next-sanity-image'
import Image from 'next/image'
import Link from 'next/link'
import { memo, useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'

import { MAX_LIST_WIDTH } from '@nxt-insurance/ui-kit/constants'
import { caption } from '@nxt-insurance/ui-kit/core/typography'
import { borderRadius } from '@nxt-insurance/ui-kit/styles/borderRadius'
import { bgColor, borderColor } from '@nxt-insurance/ui-kit/styles/colors'
import { padding } from '@nxt-insurance/ui-kit/styles/padding'

import { runQuery, sanityClientLike } from '@/cms/client'
import { borderRadiusEnum, colorEnum, spaceEnum } from '@/cms/helperTypes'
import { imageAssetQuery } from '@/cms/image'
import { webUrlSchema } from '@/cms/webUrl'
import { Icon } from '@/components/Icon'
import { EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { useUrlFromWebUrlReference } from '@/hooks/useUrlFromWebUrlReference'
import { useWebUrlRel } from '@/hooks/useWebUrlRel'
import { getContrastColor } from '@/utils/style'
import type { PortableTextTypeComponentProps } from '@portabletext/react'
import { useQuery } from '@tanstack/react-query'

import { ConfigurableTypography, configurableTypography } from './ConfigurableTypography'

const navigationItemSchema = z.object({
  _key: z.string(),
  _type: z.literal('navigationItem'),
  title: configurableTypography,
  subtitle: z.string().optional().nullable(),
  borderColor: colorEnum.optional(),
  backgroundColor: colorEnum.optional(),
  padding: spaceEnum.optional(),
  borderRadius: borderRadiusEnum.optional(),
  image: z
    .object({
      _type: z.literal('image'),
      width: z.number(),
      asset: z
        .object({
          _type: z.literal('reference'),
          _ref: z.string(),
        })
        .optional(),
    })
    .optional(),
  imageAlt: z.string().optional(),
  href: webUrlSchema.optional(),
})

export const NavigationItem = memo(
  (props: { node: PortableTextTypeComponentProps<unknown>; sectionName: string }) => {
    const { track } = useAnalytics()
    const value = useMemo(() => navigationItemSchema.parse(props.node.value), [props.node.value])
    const { imageAlt, image, href } = value
    const { data: derefedImage } = useQuery({
      queryKey: ['image', image?.asset?._ref],
      queryFn: async () => runQuery(imageAssetQuery, { id: image?.asset?._ref }),
      enabled: image?.asset !== undefined,
    })
    const imageProps = useNextSanityImage(sanityClientLike, image ?? null, {
      imageBuilder: (imageUrlBuilder) =>
        image ? imageUrlBuilder.width(image.width * 2) : imageUrlBuilder,
    })
    const parsedHref = useUrlFromWebUrlReference(href)
    const rel = useWebUrlRel(href)

    if (!value.title) {
      return null
    }

    return (
      <Link
        className={twMerge(
          'flex w-full flex-row items-center gap-x-4 border border-solid transition-opacity delay-300 ease-in-out hover:opacity-70',
          value.backgroundColor === 'uiLight' && 'hover:bg-ui-grey-50',
          padding({ space: value.padding ?? 'small' }),
          borderColor({ color: value.borderColor ?? 'uiLight' }),
          bgColor({ color: value.backgroundColor ?? 'uiLight' }),
          borderRadius({
            radius: value.borderRadius ?? 'small',
          })
        )}
        style={{
          maxWidth: MAX_LIST_WIDTH,
        }}
        href={parsedHref ?? ''}
        rel={rel}
        target={rel ? '_blank' : undefined}
        onClick={() => {
          track(EVENTS.ProductListClicked, {
            title: value.title,
            sectionName: props.sectionName,
          })
        }}
      >
        {derefedImage && image && imageProps && (
          <div style={{ width: image.width, height: image.width, overflow: 'hidden' }}>
            <Image
              {...imageProps}
              width={image.width}
              height={imageProps.height / 2}
              blurDataURL={derefedImage.metadata.lqip}
              placeholder="blur"
              alt={imageAlt ?? ''}
              className={'object-contain'}
            />
          </div>
        )}
        <div className="flex flex-1 flex-col justify-center">
          <ConfigurableTypography
            node={{
              index: 0,
              isInline: false,
              renderNode: () => null,
              value: { _key: '1', ...value.title },
            }}
            align="left"
          />
          {value.subtitle ? (
            <p className={caption({ color: 'uiDark', emphasis: 'medium', className: 'pt-2.5' })}>
              {value.subtitle}
            </p>
          ) : null}
        </div>
        <Icon
          size="small"
          icon={{
            name: getContrastColor(value.backgroundColor ?? 'uiLight').includes('Light')
              ? 'action.chevronRightUiLight_24'
              : 'action.chevronRight_24',
          }}
        />
      </Link>
    )
  },
  _isEqual
)
